/**
 * BLE Debug Panel - Real-time Bluetooth debugging
 * Only visible in development mode or via secret gesture
 */

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { bpmService } from '@/services/bpm-service';
import { X, RefreshCw, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface BLEDebugLog {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
  data?: any;
}

export function BLEDebugPanel() {
  // Só renderiza em ambiente de desenvolvimento
  const isDev = import.meta.env.DEV;
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState<BLEDebugLog[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    // Não registrar listener se não for dev
    if (!isDev) return;
    
    // Secret gesture: 5 taps anywhere (mais difícil de ativar acidentalmente)
    let tapCount = 0;
    let tapTimer: NodeJS.Timeout | null = null;

    const handleTap = () => {
      tapCount++;
      
      if (tapTimer) clearTimeout(tapTimer);
      
      tapTimer = setTimeout(() => {
        if (tapCount === 5) {
          setIsVisible(prev => {
            toast.info(prev ? 'Debug panel hidden' : 'Debug panel visible');
            return !prev;
          });
        }
        tapCount = 0;
      }, 500);
    };

    document.addEventListener('click', handleTap);
    
    return () => {
      document.removeEventListener('click', handleTap);
      if (tapTimer) clearTimeout(tapTimer);
    };
  }, [isDev]); // Sem dependência de isVisible

  // Se não for dev, não renderiza nada
  if (!isDev) return null;

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLogs(bpmService.getDebugLogs());
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleRefresh = () => {
    setLogs(bpmService.getDebugLogs());
    toast.success('Logs refreshed');
  };

  const handleClear = () => {
    bpmService.clearDebugLogs();
    setLogs([]);
    toast.success('Logs cleared');
  };

  const handleCopy = () => {
    const logText = logs
      .map(log => `[${new Date(log.timestamp).toISOString()}] ${log.level.toUpperCase()}: ${log.message}${log.data ? ` | ${JSON.stringify(log.data)}` : ''}`)
      .join('\n');
    
    navigator.clipboard.writeText(logText);
    toast.success('Logs copied to clipboard');
  };

  const connectionHealth = bpmService.getConnectionHealth();

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl h-[80vh] flex flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">BLE Debug Panel</h2>
            <Badge variant={connectionHealth.isConnected ? 'default' : 'destructive'}>
              {connectionHealth.isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsVisible(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Connection Status */}
        <div className="p-4 border-b bg-muted/30">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Device:</span>
              <span className="ml-2 font-medium">
                {connectionHealth.device || 'None'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Source:</span>
              <span className="ml-2 font-medium capitalize">
                {connectionHealth.source}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Reconnect Attempts:</span>
              <span className="ml-2 font-medium">
                {connectionHealth.reconnectAttempts}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Reading:</span>
              <span className="ml-2 font-medium">
                {connectionHealth.lastReadingAge 
                  ? `${Math.round(connectionHealth.lastReadingAge / 1000)}s ago`
                  : 'Never'}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 p-4 border-b">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
          <div className="flex-1" />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
        </div>

        {/* Logs */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-2 font-mono text-xs">
            {logs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No logs yet. Start BPM monitoring to see debug information.
              </div>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg ${
                    log.level === 'error'
                      ? 'bg-destructive/10 text-destructive'
                      : log.level === 'warn'
                      ? 'bg-yellow-500/10 text-yellow-600'
                      : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <Badge
                      variant={
                        log.level === 'error'
                          ? 'destructive'
                          : log.level === 'warn'
                          ? 'outline'
                          : 'secondary'
                      }
                      className="shrink-0 text-[10px]"
                    >
                      {log.level.toUpperCase()}
                    </Badge>
                    <span className="flex-1">{log.message}</span>
                  </div>
                  {log.data && (
                    <pre className="mt-1 text-[10px] text-muted-foreground overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t text-xs text-muted-foreground text-center">
          Triple-tap anywhere to toggle this panel
        </div>
      </Card>
    </div>
  );
}
